import React, { useState, useEffect } from 'react';
import { useSubscription } from '../hooks/useSubscription';
import { useSubscriptionPermissions } from '../hooks/useSubscriptionPermissions';
import { Lock, Crown, CheckCircle2, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface SubscriptionGuardProps {
  children: React.ReactNode;
  requiredPlan: 'Lunch' | 'Boost' | 'Scale';
  pageType?: 'admin' | 'pro' | 'premium';
  pageName?: string;
  description?: string;
  fallback?: React.ReactNode;
}

export const SubscriptionGuard: React.FC<SubscriptionGuardProps> = ({
  children,
  requiredPlan,
  pageType,
  pageName,
  description,
  fallback
}) => {
  const { plan, isLoading, canAccess } = useSubscription();
  const { isAdmin, loading: permissionsLoading } = useSubscriptionPermissions();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(true);

  // Calculer hasAccess avant les retours conditionnels, mais seulement si les données sont chargées
  const hasAccess = (isLoading || permissionsLoading) ? false : (pageType ? canAccess(pageType) : (() => {
    // Si le plan requis est Lunch, autoriser l'accès
    if (requiredPlan === 'Lunch') {
      return true;
    }
    // Pour les autres plans, vérifier la hiérarchie
    const planHierarchy = { 'Lunch': 0, 'Boost': 1, 'Scale': 2 };
    // Normaliser le plan utilisateur pour garantir la cohérence
    const normalizedUserPlan = plan ? (() => {
      const planLower = plan.toLowerCase();
      if (planLower.includes('scale')) return 'Scale';
      if (planLower.includes('boost')) return 'Boost';
      if (planLower.includes('launch') || planLower.includes('lunch')) return 'Lunch';
      return plan.charAt(0).toUpperCase() + plan.slice(1).toLowerCase();
    })() : 'Lunch';
    
    const userPlanLevel = planHierarchy[normalizedUserPlan as keyof typeof planHierarchy] || 0;
    const requiredPlanLevel = planHierarchy[requiredPlan as keyof typeof planHierarchy] || 0;
    
    // Log pour debug
    if (requiredPlan === 'Scale' || pageName === 'assistant') {
      console.log('🔍 SubscriptionGuard Debug:', {
        plan,
        normalizedUserPlan,
        requiredPlan,
        pageName,
        userPlanLevel,
        requiredPlanLevel,
        hasAccess: userPlanLevel >= requiredPlanLevel,
        isLoading,
        permissionsLoading
      });
    }
    
    return userPlanLevel >= requiredPlanLevel;
  })());

  // Rediriger vers le dashboard si le modal est fermé sans accès
  useEffect(() => {
    if (!isLoading && !permissionsLoading && !hasAccess && !modalOpen) {
      navigate('/dashboard', { replace: true });
    }
  }, [hasAccess, modalOpen, navigate, isLoading, permissionsLoading]);

  if (isLoading || permissionsLoading) {
    return <>{children}</>;
  }

  // Les admins ont toujours accès à toutes les pages
  if (isAdmin) {
    console.log('✅ SubscriptionGuard: Admin bypass - accès autorisé');
    return <>{children}</>;
  }

  // Debug pour la page assistant
  if (pageName === 'assistant') {
    console.log('🔍 SubscriptionGuard - Assistant page:', {
      plan,
      requiredPlan,
      hasAccess,
      isLoading,
      permissionsLoading,
      isAdmin
    });
  }

  // Si pas d'accès, afficher un modal par-dessus la page
  if (!hasAccess) {
    // Si le modal est fermé, rediriger immédiatement (fallback de sécurité)
    if (!modalOpen) {
      navigate('/dashboard', { replace: true });
      return null;
    }
    if (fallback) {
      return <>{fallback}</>;
    }

    const planDisplayName = plan === 'Lunch' ? 'Lunch' : plan === 'Boost' ? 'Boost' : plan === 'Scale' ? 'Scale' : 'Lunch';
    
    // Obtenir les fonctionnalités selon le plan requis
    const getFeatureDescription = () => {
      switch (requiredPlan) {
        case 'Boost':
          return {
            items: [
              'Calendrier interactif',
              'Gestion des tâches avancée',
              'Système de parrainage',
              'Support prioritaire'
            ]
          };
        case 'Scale':
          return {
            items: [
              'Everything in Boost',
              'Statistiques avancées',
              'Système de facturation',
              'Rapports détaillés',
              'Support 24/7'
            ]
          };
        default:
          return {
            items: []
          };
      }
    };

    const features = getFeatureDescription();

    return (
      <>
        {children}
        {/* Modal de blocage - z-index plus bas pour ne pas cacher le Composer */}
        <div className="fixed inset-0 z-[49] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => {
              setModalOpen(false);
              navigate('/dashboard');
            }}
            aria-hidden="true"
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="upgrade-modal-title"
            className="
              relative w-full max-w-lg
              rounded-3xl overflow-hidden
              shadow-2xl
              border border-[rgb(var(--border))]
              bg-[rgb(var(--card))]/90
              backdrop-blur-xl
            "
          >
            <div className="h-1 w-full bg-gradient-to-r from-indigo-500 via-fuchsia-500 to-amber-500" />

            <div className="px-6 pt-6 pb-3 flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div
                  className="
                    p-2.5 rounded-2xl
                    bg-gradient-to-br from-indigo-600 to-fuchsia-600
                    text-[rgb(var(--fg))] shadow-md shadow-indigo-900/20
                  "
                  aria-hidden="true"
                >
                  <Crown size={20} />
                </div>
                <div>
                  <h3 id="upgrade-modal-title" className="text-xl font-semibold text-[rgb(var(--fg))]">
                    Upgrade required
                  </h3>
                  <p className="mt-0.5 text-sm text-[rgb(var(--muted))]">
                    Unlock this feature on the <span className="font-medium">{requiredPlan}</span> plan.
                  </p>
                </div>
              </div>

              <button
                onClick={() => {
                  setModalOpen(false);
                  navigate('/dashboard');
                }}
                className="p-2 rounded-lg hover:bg-[rgb(var(--card))] text-[rgb(var(--muted))]"
                aria-label="Close upgrade modal"
              >
                <X size={18} />
              </button>
            </div>

            <div className="px-6 pb-6">
              <div className="mb-4">
                <span
                  className="
                    inline-flex items-center gap-1.5
                    text-xs font-medium
                    px-2.5 py-1.5 rounded-full
                    bg-gradient-to-r from-indigo-600 to-fuchsia-600 text-[rgb(var(--fg))]
                    shadow-md shadow-indigo-900/10
                  "
                >
                  <Lock size={14} />
                  {requiredPlan} features
                </span>
              </div>

              <div
                className="
                  rounded-2xl p-4
                  border border-[rgb(var(--border))]
                  bg-[rgb(var(--card))]/70
                "
              >
                <p className="text-sm mb-3 text-[rgb(var(--muted))]">
                  You'll get:
                </p>

                {features.items.length > 0 ? (
                  <ul className="space-y-2">
                    {features.items.map((item, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-[rgb(var(--muted))]">
                        <CheckCircle2 className="mt-0.5 flex-none text-green-500" size={16} />
                        {item}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2 text-sm text-[rgb(var(--muted))]">
                      <CheckCircle2 className="mt-0.5 flex-none text-green-500" size={16} />
                      Premium features & higher limits
                    </li>
                  </ul>
                )}
              </div>

              <div className="mt-6 flex flex-col sm:flex-row sm:justify-end gap-2.5">
                <button
                  onClick={() => {
                    setModalOpen(false);
                    navigate('/dashboard');
                  }}
                  className="px-4 py-2 rounded-xl border border-[rgb(var(--border))] bg-[rgb(var(--card))] text-[rgb(var(--muted))] hover:bg-[rgb(var(--card))] transition"
                >
                  Not now
                </button>
                <button
                  onClick={() => {
                    setModalOpen(false);
                    navigate('/upgrade');
                  }}
                  className="px-4 py-2 rounded-xl text-[rgb(var(--fg))] bg-gradient-to-r from-indigo-600 to-fuchsia-600 hover:shadow-lg hover:shadow-indigo-900/20 transition"
                >
                  See plans
                </button>
              </div>

              <p className="mt-3 text-center text-xs text-[rgb(var(--muted))]">
                You can change or cancel your plan anytime.
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return <>{children}</>;
};

export default SubscriptionGuard;